import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  apiClient,
  ServiceRequestDetailsLoaderResult,
} from "~/services/apiClient";
import {
  FilesReturnType,
  GETDBFileObj,
  LoaderData,
  ServiceRequestEntityDetails,
} from "~/types";
import { ENVS, INTENTS, ROUTES, loadCookiesData } from "~/utils";
import * as APITypes from "~/API";
import { S3ServiceLambda } from "~/services/s3";

export async function ServiceRequestDetailsPageLoader({
  request,
  params,
}: LoaderFunctionArgs) {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) redirect("/explore");
  const userUID = user?.uid || "";
  const authAccessInfo = await loadCookiesData(userUID);
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const requestID = params.requestID || "";

  const result = await apiClient.loader<ServiceRequestDetailsLoaderResult>(
    "service-request-details",
    {
      id: requestID,
      ownerID,
      ownerType,
      userID: userUID,
    },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load service request details:", result.error);
    return redirect(ROUTES.serviceRequest);
  }

  const data = result.data;
  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const entityType = data.entityType;
  const entityID = data.serviceRequest.entityID || "";

  const files = await S3ServiceLambda.list({
    entityType: data.entityType as APITypes.EntityType,
    entityID,
  });

  return {
    serviceRequest: data.serviceRequest,
    comments: data.comments,
    entityDetails: data.entityDetails,
    entityType,
    fileList: { success: true, data: [] } as LoaderData<GETDBFileObj[]>,
    files: files.data,
    canEditRequest: data.permissions.canEditRequest,
    canDeleteRequest: data.permissions.canDeleteRequest,
    canReadRequest: data.permissions.canReadRequest,
    isAdminOrSuperAdmin,
    subscriptionPlan: data.subscriptionPlan,
    hasServiceRequestAccess: data.hasServiceRequestAccess,
  };
}

export type ServiceRequestDetailsPageLoaderType = {
  serviceRequest: APITypes.ServiceRequest;
  comments: APITypes.Comments[];
  entityDetails: {
    [ServiceRequestEntityDetails.property]?: LoaderData<APITypes.Property>;
    [ServiceRequestEntityDetails.building]?: LoaderData<APITypes.Building>;
    [ServiceRequestEntityDetails.unit]?: LoaderData<APITypes.Unit>;
    [ServiceRequestEntityDetails.parcel]?: LoaderData<APITypes.Parcel>;
  };
  entityType: APITypes.EntityType;
  fileList: LoaderData<GETDBFileObj[]>;
  files: FilesReturnType;
  canEditRequest: boolean;
  canDeleteRequest: boolean;
  canReadRequest: boolean;
  isAdminOrSuperAdmin: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
};

export const ServiceRequestDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return { success: false, error: "Not authenticated" };
  const requestID = params.requestID || "";

  switch (intent) {
    case INTENTS._edit_service_request: {
      const payload = JSON.parse(formData.get("body") as string);
      const result = await apiClient.sendIntent<APITypes.ServiceRequest>(
        INTENTS._edit_service_request,
        { id: requestID, requestID, ...payload }
      );
      if (!result.success) return { success: false, error: result.error };
      return result;
    }
    case INTENTS._add_comment: {
      const payload = JSON.parse(formData.get("body") as string);
      const result = await apiClient.sendIntent<APITypes.Comments>(
        INTENTS._add_comment,
        { id: requestID, requestID, ...payload }
      );
      if (!result.success) return { success: false, error: result.error };
      return result;
    }
    case INTENTS._update_request_status: {
      const payload = JSON.parse(formData.get("body") as string);
      const result = await apiClient.sendIntent<APITypes.ServiceRequest>(
        INTENTS._update_request_status,
        { id: requestID, requestID, ...payload }
      );
      if (!result.success) return { success: false, error: result.error };
      return result;
    }
    case INTENTS._remove_service_request: {
      const bodyStr = formData.get("body") as string;
      const payload = bodyStr ? JSON.parse(bodyStr) : {};
      const result = await apiClient.sendIntent<{ deleted: boolean; id: string }>(
        INTENTS._remove_service_request,
        { id: requestID, requestID, ...payload }
      );
      if (!result.success) return { success: false, error: result.error };
      return redirect(ROUTES.serviceRequest);
    }
    case INTENTS._delete_file: {
      const key: string = formData.get("body") as string;
      const result = await S3ServiceLambda.remove({ key });
      return result;
    }
    case INTENTS._add_message: {
      const payload = JSON.parse(formData.get("body") as string);
      const result = await apiClient.sendIntent<APITypes.Message>(
        INTENTS._add_message,
        { ...payload }
      );
      if (!result.success) return { success: false, error: result.error };
      return result;
    }
  }
  return null;
};
